import { NumberView, NumberViewProps } from '../number-view';
import { BASE_TOKEN_CONFIG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useBaseTokens } from '@/libs/react-sdk/api/use-base-tokens';
import { HTMLAttributes, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Cap a loading flag: returns `loading` but forces false after `ms` of
 * continuous loading. Prevents an indefinite spinner when an upstream query
 * (e.g. base-tokens) keeps retrying a failing endpoint — the value falls back
 * to 0 instead of spinning forever. Resets when loading clears.
 */
const useTimedLoading = (loading: boolean, ms = 6000) => {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!loading) {
      setExpired(false);
      return;
    }
    const timer = setTimeout(() => setExpired(true), ms);
    return () => clearTimeout(timer);
  }, [loading, ms]);
  return loading && !expired;
};

export type CurrencyViewProps = HTMLAttributes<HTMLSpanElement> &
  NumberViewProps & {
    children?: ReactNode;
    showIcon?: boolean;
    showUnit?: boolean;
    baseTokenAddress?: string;
    unitClassName?: string;
    numberClassName?: string;
  };

export const CurrencyView = ({
  value,
  decimal,
  precision = 2,
  isChange = false,
  isLoading = false,
  animated = false,
  showSign = true,
  compact = true,
  baseTokenAddress,
  showIcon,
  showUnit,
  children,
  className,
  unitClassName,
  numberClassName,
  ...attrs
}: CurrencyViewProps) => {
  const { t } = useTranslation();
  const baseTokens = useBaseTokens(api);
  const baseToken = useAppSettingStore(state => state.baseToken);
  const finalToken = useMemo(
    () =>
      BASE_TOKEN_CONFIG.find(item => item.address.toLowerCase() === baseTokenAddress?.toLowerCase()) || baseToken,
    [baseTokenAddress, baseToken]
  );
  const baseTokenInfo = useMemo(
    () => baseTokens.data?.baseTokens?.find(item => item.baseTokenAddress?.toLowerCase() === finalToken.address?.toLowerCase()),
    [baseTokens.data?.baseTokens, finalToken.address]
  );
  const finalDecimal = useMemo(
    () => Number(decimal ?? baseTokenInfo?.baseTokenDecimals ?? finalToken.decimals),
    [decimal, baseTokenInfo?.baseTokenDecimals, finalToken.decimals]
  );
  const finalPrecision = useMemo(() => Number(precision), [precision]);
  const finalIsLoading = useTimedLoading(isLoading || baseTokens.isLoading);
  return (
    <span {...attrs} className={cn('inline-flex items-center gap-.5 flex-nowrap', className)}>
      {showIcon && finalToken.icon && <finalToken.icon className="size-[1em] flex-shrink-0" />}
      <NumberView
        value={value}
        decimal={finalDecimal}
        precision={finalPrecision}
        isChange={isChange}
        isLoading={finalIsLoading}
        animated={animated}
        showSign={showSign}
        compact={compact}
        className={numberClassName}
      />
      {children}
      {showUnit && <span className={cn(unitClassName)}>{t(finalToken.translateKey)}</span>}
    </span>
  );
};
